import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Tooltip, Chip, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Checkbox, FormControlLabel, CircularProgress, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { manufacturingAPI } from '../services/api';
import OrderTasks from '../components/common/OrderTasks';

const fmtEUR = (v) => (Number(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 });

const STATUS_LABEL = { open: 'Abierta', closed: 'Cerrada' };

export default function ManufacturingOrders({ projectId }) {
  const [orders, setOrders] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create/edit order dialog
  const [orderDialog, setOrderDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', status: 'open', notes: '' });
  const [formError, setFormError] = useState('');

  // Assign offers dialog
  const [assignDialog, setAssignDialog] = useState(null); // order id or null
  const [selectedOffers, setSelectedOffers] = useState([]);

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ordResp, offResp] = await Promise.all([
        manufacturingAPI.list(projectId),
        manufacturingAPI.assignableOffers(projectId),
      ]);
      setOrders(ordResp.data);
      setAssignable(offResp.data);
    } catch (e) {
      console.error(e);
      setError('Error al cargar las órdenes de fabricación.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  const openNewOrder = async () => {
    setEditId(null);
    setFormError('');
    let code = '';
    try {
      const resp = await manufacturingAPI.nextCode(projectId);
      code = resp.data.code || '';
    } catch { /* si falla, el usuario introduce el código manualmente */ }
    setForm({ code, name: '', status: 'open', notes: '' });
    setOrderDialog(true);
  };

  const openEditOrder = (o) => {
    setEditId(o.id);
    setFormError('');
    setForm({ code: o.code || '', name: o.name || '', status: o.status || 'open', notes: o.notes || '' });
    setOrderDialog(true);
  };

  const saveOrder = async () => {
    if (!form.code || !form.code.trim()) { setFormError('El número de OF es obligatorio.'); return; }
    try {
      setFormError('');
      if (editId) {
        await manufacturingAPI.update(editId, form);
      } else {
        await manufacturingAPI.create(projectId, form);
      }
      setOrderDialog(false);
      await reload();
    } catch (e) {
      setFormError(e.response?.data?.detail || 'Error al guardar la orden de fabricación.');
    }
  };

  const toggleStatus = async (o) => {
    await manufacturingAPI.update(o.id, { status: o.status === 'closed' ? 'open' : 'closed' });
    await reload();
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      await manufacturingAPI.delete(deleteConfirm);
      setDeleteConfirm(null);
      await reload();
    }
  };

  const openAssign = (orderId) => {
    setSelectedOffers([]);
    setAssignDialog(orderId);
  };

  const toggleOffer = (id) => {
    setSelectedOffers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const saveAssign = async () => {
    if (selectedOffers.length > 0) {
      await manufacturingAPI.assignOffers(assignDialog, selectedOffers);
    }
    setAssignDialog(null);
    await reload();
  };

  const unassign = async (orderId, offerId) => {
    await manufacturingAPI.unassignOffer(orderId, offerId);
    await reload();
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 2, backgroundColor: '#fff3e0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Órdenes de Fabricación</Typography>
            <Typography variant="body2" color="textSecondary">
              Agrupa manualmente las ofertas de la ingeniería de detalle en órdenes de fabricación.
              Los pedidos generados a partir de esas ofertas mostrarán su orden de fabricación.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewOrder}>
            Nueva orden
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {orders.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No hay órdenes de fabricación. Crea una con "Nueva orden".
          </Typography>
        </Paper>
      )}

      {orders.map((o) => (
        <Paper key={o.id} variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fafafa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight="bold">{o.code}</Typography>
              {o.name && <Typography variant="body2" color="textSecondary">{o.name}</Typography>}
              <Chip
                size="small"
                label={STATUS_LABEL[o.status] || o.status}
                color={o.status === 'closed' ? 'default' : 'success'}
              />
              <Chip size="small" variant="outlined" label={`${o.offer_count} ofertas · ${fmtEUR(o.total_amount)} EUR`} />
            </Box>
            <Box>
              <Tooltip title="Añadir ofertas">
                <span>
                  <IconButton size="small" color="primary" onClick={() => openAssign(o.id)} disabled={o.status === 'closed'}>
                    <PlaylistAddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={o.status === 'closed' ? 'Reabrir orden' : 'Cerrar orden'}>
                <IconButton size="small" onClick={() => toggleStatus(o)}>
                  {o.status === 'closed' ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => openEditOrder(o)}><EditIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton size="small" color="error" onClick={() => setDeleteConfirm(o.id)}><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
          </Box>
          {o.notes && (
            <>
              <Divider />
              <Typography variant="body2" sx={{ px: 2, py: 1, whiteSpace: 'pre-wrap', color: 'text.secondary' }}>{o.notes}</Typography>
            </>
          )}
          <Divider />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#c62828' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Concepto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Proveedor</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Importe (EUR)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#fff', width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {o.offers.map((of) => (
                  <TableRow key={of.id} hover sx={of.is_obsolete ? { backgroundColor: '#ef9a9a' } : undefined}>
                    <TableCell>{of.equipment_name || '—'}</TableCell>
                    <TableCell>{of.supplier_name || '—'}</TableCell>
                    <TableCell align="right">{fmtEUR(of.amount)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Quitar de la orden">
                        <span>
                          <IconButton size="small" color="error" onClick={() => unassign(o.id, of.id)} disabled={o.status === 'closed'}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {o.offers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
                        Sin ofertas asignadas. Usa el botón "Añadir ofertas".
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <OrderTasks order={o} />
        </Paper>
      ))}

      {/* Dialog crear/editar orden */}
      <Dialog open={orderDialog} onClose={() => setOrderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Editar orden de fabricación' : 'Nueva orden de fabricación'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{formError}</Alert>}
          <TextField
            fullWidth label="Número de OF" sx={{ mt: 1 }}
            value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
            helperText="Se propone correlativo (p. ej. 26001-001); puedes modificarlo. Es la referencia que se usará en dibujos, pedidos, horas y albaranes."
          />
          <TextField
            fullWidth label="Nombre / descripción" sx={{ mt: 2 }}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            select fullWidth label="Estado" sx={{ mt: 2 }}
            value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            <MenuItem value="open">Abierta</MenuItem>
            <MenuItem value="closed">Cerrada</MenuItem>
          </TextField>
          <TextField
            fullWidth label="Notas" multiline rows={3} sx={{ mt: 2 }}
            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveOrder}>{editId ? 'Actualizar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog asignar ofertas */}
      <Dialog open={assignDialog !== null} onClose={() => setAssignDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Añadir ofertas a la orden</DialogTitle>
        <DialogContent>
          {assignable.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
              No hay ofertas disponibles para asignar (todas están ya en una orden o marcadas como obsoletas).
            </Typography>
          ) : (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Concepto</TableCell>
                    <TableCell>Proveedor</TableCell>
                    <TableCell align="right">Importe (EUR)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignable.map((of) => (
                    <TableRow key={of.id} hover sx={{ cursor: 'pointer' }} onClick={() => toggleOffer(of.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedOffers.includes(of.id)} onChange={() => toggleOffer(of.id)} />
                      </TableCell>
                      <TableCell>{of.equipment_name || '—'}</TableCell>
                      <TableCell>{of.supplier_name || '—'}</TableCell>
                      <TableCell align="right">{fmtEUR(of.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveAssign} disabled={selectedOffers.length === 0}>
            Añadir {selectedOffers.length > 0 ? `(${selectedOffers.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar borrado */}
      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Eliminar orden de fabricación</DialogTitle>
        <DialogContent>
          <Typography>Las ofertas asignadas volverán a quedar sueltas. ¿Continuar?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
